import Constants from '@/Constants';
import { GetClassificationsData } from '@/models/GetClassificationsData';
import { analyticsService } from '@/services/AnalyticsService';
import { ApplicationState } from '@/store';
import { getClassificationsRequest } from '@/store/classification/action';
import { Classification } from '@/store/classification/types';
import { clearRate, getRateRequest, setContentRateRequest, setCourseRateRequest, setProgramRateRequest } from '@/store/rating/actions';
import { RateData } from '@/store/rating/types';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import _ from 'underscore';
import ActionFeedBackModal from '../ActionFeedbackModal';
import './style.scss';

interface Props {
    entityId: number;
    entityName: string;
    entityType: string;
    title: string;
    analyticEvent: string;

    onSendPress?: Function;
    modal?: boolean;
    classification?: boolean;
}

const StudentRating: React.FC<Props> = (props: Props) => {
    const { entityId, entityName, entityType, onSendPress, modal, classification, title, analyticEvent } = props;

    const { strings, personId, loading, ownRating, organizationId, ownClassificationsList, rate, success, error } = useSelector(
        (state: ApplicationState) => {
            return {
                strings: state.locale.data,
                personId: state.identity.data.person.id,
                loading: state.rating.loading,
                ownRating: state.rating.data.rating,
                organizationId: state.identity.data.user && state.identity.data.user.orgId,
                ownClassificationsList: state.classifications.data.classificationsList,
                rate: state.rating.data,
                success: state.rating.submittedRating,
                error: state.rating.errorOnSubmit,
            };
        },
    );

    const classEnrollmentId: number = parseInt(useParams<{ enrollmentId: string }>().enrollmentId);

    const [rating, setRating] = useState<number>(0);
    const [classificationsList, setClassificationsList] = useState<Classification[]>([] as Classification[]);

    const [initialRating, setInitialRating] = useState<number>(0);
    const [initialClassificationsList, setInitialClassificationsList] = useState<Classification[]>([] as Classification[]);

    const dispatch = useDispatch();

    enum ContentClassification {
        CONCEPTUAL = 1,
        PRACTICAL = 2,
        CURRENT = 3,
        OUTDATED = 4,
        BASIC = 5,
        SPECIALIZED = 6,
    }

    const conceptualPratical: number[] = [ContentClassification.CONCEPTUAL, ContentClassification.PRACTICAL];
    const basicEspecialized: number[] = [ContentClassification.BASIC, ContentClassification.SPECIALIZED];
    const outdatedCurrent: number[] = [ContentClassification.OUTDATED, ContentClassification.CURRENT];

    const getUIClassification = () => {
        return ownClassificationsList.map((classification: Classification) => {
            classification.isSelected = false;
            if (rate.classifications?.length > 0) {
                if (rate.classifications.find((c) => c.id === classification.id)) {
                    classification.isSelected = true;
                }
            }
            return { ...classification };
        });
    };

    useEffect((): void => {
        if (ownClassificationsList) {
            setClassificationsList(getUIClassification());
            setInitialClassificationsList(getUIClassification());
        }
    }, [ownClassificationsList]);

    useEffect(() => {
        if (rate.classifications?.length > 0) {
            setClassificationsList(getUIClassification());
            setInitialClassificationsList(getUIClassification());
        }
    }, [rate.classifications]);

    useEffect((): void => {
        if (ownRating) {
            setRating(ownRating);
            setInitialRating(ownRating);
        }
    }, [ownRating]);

    useEffect(() => {
        const rateRequestUris = {
            [Constants.entityTypes.CONTENT]: Constants.SOCIAL_CONTENT_OWN_RATE_URI,
            [Constants.entityTypes.COURSE]: Constants.SOCIAL_COURSE_OWN_RATE_URI,
            [Constants.entityTypes.PROGRAM]: Constants.SOCIAL_PROGRAM_OWN_RATE_URI,
        };

        dispatch(getRateRequest(rateRequestUris[entityType], entityId, personId as number));
        getClassifications();

        return () => {
            dispatch(clearRate());
        };
    }, [strings._LANGUAGE]);

    const getClassifications = () => {
        dispatch(
            getClassificationsRequest({
                organizationId,
                applicationId: Constants.APP_ID,
            } as GetClassificationsData),
        );
    };

    const handleOnStarClick = (starIndex: number, event: SyntheticEvent) => {
        event.preventDefault();
        setRating((prevRating) => {
            prevRating !== starIndex && !classification && sendNewRate(starIndex);
            return starIndex;
        });
    };

    const disabledSendButton = (): boolean => {
        return _.isEqual(initialRating, rating) && _.isEqual(initialClassificationsList, classificationsList);
    };

    const onRatingSendPress = (): void => {
        onSendPress && onSendPress(true);
        sendNewRate(rating);
    };

    const sendNewRate = (newRating: number) => {
        switch (entityType) {
            case Constants.entityTypes.CONTENT:
                dispatch(
                    setContentRateRequest({
                        entityId,
                        contentTitle: entityName,
                        criteriaId: `content_${entityId}`,
                        criteriaTitle: '',
                        rating: newRating,
                        personId,
                        classifications: getSelectedClassificationList(),
                    } as RateData),
                );
                break;
            case Constants.entityTypes.COURSE:
                dispatch(
                    setCourseRateRequest({
                        entityId,
                        contentTitle: entityName,
                        criteriaId: `course_${entityId}`,
                        criteriaTitle: '',
                        rating: newRating,
                        personId,
                        classifications: getSelectedClassificationList(),
                        classEnrollmentId,
                    } as RateData),
                );
                break;
            case Constants.entityTypes.PROGRAM:
                dispatch(
                    setProgramRateRequest({
                        entityId,
                        contentTitle: entityName,
                        criteriaId: `program_${entityId}`.toLowerCase(),
                        criteriaTitle: '',
                        rating: newRating,
                        personId,
                        classifications: getSelectedClassificationList(),
                        classEnrollmentId,
                    } as RateData),
                );
                break;
        }

        analyticsService.logEvent(analyticEvent, {
            ownRating: rating,
            entityId,
            entityName,
            entityType,
        });
    };

    const getSelectedClassificationList = (): Classification[] => {
        return classificationsList.filter((item: Classification) => item.isSelected);
    };

    const renderStarsByRating = () => {
        const starsNumber = 5;
        const stars = [];
        const star: string = Constants.icons.STAR;
        const starBorder: string = Constants.icons.STAR_BORDER;

        for (let i = 1; i <= starsNumber; i++) {
            const starIcon: string = i > rating ? starBorder : star;
            stars.push(
                <a href="" key={i} onClick={(event: SyntheticEvent) => handleOnStarClick(i, event)}>
                    <i className="material-icons-outlined rating-star-container--rating-star">{starIcon}</i>
                </a>,
            );
        }

        return <div className="rating-star-container">{stars}</div>;
    };

    const getTagStyle = (classification: Classification) => {
        return classification.isSelected ? 'tags tags--selected' : 'tags';
    };

    const onClassificationPressHandler = (classificationId: number): void => {
        const classificationListCopy: Classification[] = [...classificationsList];
        const classificationCopy: Classification = classificationListCopy.find((c: Classification) => c.id === classificationId) as Classification;
        classificationCopy.isSelected = !classificationCopy.isSelected;
        setClassificationsList(classificationListCopy);
    };

    const handleOnClose = (event: SyntheticEvent) => {
        event.preventDefault();
        onSendPress && onSendPress(false);
    };

    const renderOptions = (options: number[]) => {
        return (
            <div className="student-rating-container__optionsContainer">
                {classificationsList
                    .filter((cl) => options.includes(cl.id))
                    .map((classification: Classification) => (
                        <button
                            className={getTagStyle(classification)}
                            key={classification.id}
                            onClick={() => onClassificationPressHandler(classification.id)}
                            type="button"
                        >
                            {classification.name}
                        </button>
                    ))}
            </div>
        );
    };

    return (
        <>
            {classification && (
                <ActionFeedBackModal
                    error={error}
                    errorMessage={strings.ERROR_WHILE_SAVE_DATA}
                    loading={loading}
                    onRetry={onRatingSendPress}
                    success={success}
                    successMessage={strings.SUCCESS_CLASSIFICATION_MESSAGE}
                />
            )}
            <div className={!modal ? 'main' : ''} id={Constants.RATING_SECTION_ID}>
                <div className={!modal ? 'rating-section' : ''}>
                    <div className="student-rating-container">
                        {modal && (
                            <div className="student-rating-container__rating-header">
                                <span></span>
                                <a href="" onClick={(e) => handleOnClose(e)}>
                                    <i className="material-icons-outlined">{Constants.icons.CLOSE}</i>
                                </a>
                            </div>
                        )}
                        <div className="student-rating-container__rating-title">
                            <span>{title}</span>
                        </div>
                        <div className="student-rating-container__rating-instruction">
                            <span>{strings.RATING_INSTRUCTION}</span>
                        </div>
                        <div>{renderStarsByRating()}</div>
                        {classification && (
                            <>
                                <div className="student-rating-container__classify-title">
                                    <span>{strings.WHAT_DID_YOU_THINK}</span>
                                </div>
                                <div className="student-rating-container__classify-instruction">
                                    <span>{strings.SELECT_AS_MANY_OPTIONS_AS_YOU_WANT}</span>
                                </div>
                                {classificationsList && classificationsList.length > 0 && (
                                    <>
                                        {renderOptions(conceptualPratical)}
                                        {renderOptions(basicEspecialized)}
                                        {renderOptions(outdatedCurrent)}
                                    </>
                                )}
                                <button
                                    className="btn btn-primary submit-button"
                                    disabled={disabledSendButton()}
                                    onClick={onRatingSendPress}
                                    type="button"
                                >
                                    {strings.SEND_REVIEW}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default StudentRating;
